import qs from 'qs'
import { useEffect, useState } from 'react'

import { postMFAAuth } from '@/api'
import { useOffice } from '@/hooks/useOffice'
import { useUserExpiry } from '@/hooks/useUser'
import { extractMFA } from '@/lib/helpers'
import { useMixpanel } from '@/services/MixpanelService'
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from '@/storage/constants'
import { clearCachedUser, saveDataInStorage } from '@/storage/storageRepository'

export const useMFA = () => {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<any | null>(null)
  const { token: tokenFromUrl } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  })
  const mixpanel = useMixpanel()
  const [shouldSendEvent, setShouldSendEvent] = useState(false)
  const [token, setToken] = useState<string | null>(tokenFromUrl)
  const { isUserExpired, isExpiryLoading } = useUserExpiry()
  const [emailBody, setEmailBody] = useState<string | null>(null)

  useEffect(() => {
    if (!isExpiryLoading) {
      if (isUserExpired && emailBody) {
        const { isMFAEmail, token } = extractMFA(emailBody)
        if (isMFAEmail && token) {
          setToken(token)
          setState('loading')
        } else {
          setState('error')
          setError('Could not find MFA token in email')
        }
      } else {
        setState('success')
      }
    }
  }, [isUserExpired, isExpiryLoading, emailBody])

  useOffice({
    onReadyCallback: () => {
      if (!isExpiryLoading && !isUserExpired) {
        setState('success')
      }
    },
    onItemBodyChangedCallback: (emailBody) => {
      setEmailBody(emailBody)
      if (!isExpiryLoading && !isUserExpired) {
        setState('success')
      }
    },
  })

  useEffect(() => {
    if (token && token !== '') {
      postMFAAuth(token)
        .then((result) => {
          if ('error' in result) {
            setState('error')
            setError(result.error as string)
            return
          }
          clearCachedUser()
          saveDataInStorage(TOKEN_KEY, result.token)
          saveDataInStorage(REFRESH_TOKEN_KEY, result.refreshToken)
          setShouldSendEvent(true)
        })
        .catch((error) => {
          setState('error')
          setError(error)
        })
    }
  }, [token])

  useEffect(() => {
    if (mixpanel && shouldSendEvent) {
      mixpanel.track(
        'Outlook MFA login completed',
        {},
        { send_immediately: true }
      )
      setShouldSendEvent(false)
      setState('success') // ensure the event is sent
    }
  }, [mixpanel, shouldSendEvent])

  return { state, error }
}
